import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

type Props = {
  onEntryActive?: (entry: IntersectionObserverEntry) => void
  observerOptions?: IntersectionObserverInit
  inViewCondition?: (entry: IntersectionObserverEntry) => boolean
}

const options: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5,
}

const defaultInViewCondition = (entry: IntersectionObserverEntry): boolean =>
  entry.isIntersecting

export const useInViewport = <T extends HTMLElement>(
  props?: Props,
): [RefObject<T | null>, boolean] => {
  const elementRef = useRef<T | null>(null)
  const [inViewport, setInViewport] = useState<boolean>(false)
  const inViewCondition = props?.inViewCondition || defaultInViewCondition
  const observerOptions = props?.observerOptions
  const onEntryActive = props?.onEntryActive

  const handleIntersect = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      const isIntersecting = inViewCondition(entry)
      setInViewport(isIntersecting)
      if (isIntersecting) {
        onEntryActive?.(entry)
      }
    },
    [inViewCondition, onEntryActive],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersect, {
      ...options,
      ...observerOptions,
    })
    if (elementRef.current) {
      observer.observe(elementRef.current)
    }
    return () => {
      observer.disconnect()
    }
  }, [observerOptions, handleIntersect])

  return [elementRef, inViewport]
}
